import { create } from 'zustand';

import { Label, PanelMode } from 'src/apps/dashboard/pages/label-management/types';
import {
	fetchLabels as fetchLabelsApi,
	saveLabel as saveLabelApi
} from 'src/apps/dashboard/services/label-management';
import { LABEL_MANAGEMENT_LABELS } from 'src/common/providers/reactQueryProvider/QueryKeys';
import { queryClient } from 'src/common/providers/reactQueryProvider/ReactQueryProvider';
import { Logger } from 'src/common/services/Logger';

interface LabelManagementState {
	labels: Label[];
	isLoading: boolean;
	isSaving: boolean;
	isDeleting: boolean;
	error: string | null;
	isPanelOpen: boolean;
	panelMode: PanelMode;
	activeLabel: Label | null;
	labelToDelete: Label | null;

	fetchLabels: (productId: string) => Promise<void>;
	saveLabel: (productId: string, label: Label) => Promise<void>;
	deleteLabel: () => Promise<void>;
	openAddPanel: () => void;
	openEditPanel: (label: Label) => void;
	closePanel: () => void;
	requestDelete: (label: Label) => void;
	cancelDelete: () => void;
	reset: () => void;
}

const initialState = {
	labels: [],
	isLoading: true,
	isSaving: false,
	isDeleting: false,
	error: null,
	isPanelOpen: false,
	panelMode: PanelMode.ADD,
	activeLabel: null,
	labelToDelete: null
};

export const useLabelManagementStore = create<LabelManagementState>((set, get) => ({
	...initialState,

	fetchLabels: async (productId: string) => {
		set({ isLoading: true, error: null });

		try {
			const labels = await queryClient.fetchQuery({
				queryKey: [LABEL_MANAGEMENT_LABELS, productId],
				queryFn: () => fetchLabelsApi(productId)
			});

			set({ labels, isLoading: false });
		} catch (error) {
			Logger.error('LabelManagementStore -> fetchLabels failed:', error);
			set({
				isLoading: false,
				error: 'Failed to load labels'
			});
		}
	},

	saveLabel: async (productId: string, label: Label) => {
		set({ isSaving: true });

		try {
			await saveLabelApi(productId, label);
			await queryClient.invalidateQueries({
				queryKey: [LABEL_MANAGEMENT_LABELS]
			});
			await get().fetchLabels(productId);
			set({ isPanelOpen: false, activeLabel: null, isSaving: false });
		} catch (error) {
			Logger.error('LabelManagementStore -> saveLabel failed:', error);
			set({ isSaving: false });
			throw error;
		}
	},

	deleteLabel: async () => {
		// Implemented in EW-14991
	},

	openAddPanel: () => {
		set({ isPanelOpen: true, panelMode: PanelMode.ADD, activeLabel: null });
	},

	openEditPanel: (label: Label) => {
		set({ isPanelOpen: true, panelMode: PanelMode.EDIT, activeLabel: label });
	},

	closePanel: () => {
		set({ isPanelOpen: false, activeLabel: null });
	},

	requestDelete: (label: Label) => {
		set({ labelToDelete: label });
	},

	cancelDelete: () => {
		set({ labelToDelete: null });
	},

	reset: () => set(initialState)
}));
